/** A bleed ticks once a second. */
export const BLEED_TICK_MS = 1000;
/**
 * How long a boss's announced ability is pushed back by a stun (ADR 0048):
 * a boss is never stunned, but a well-timed bash buys its victims a breath.
 * Once per ability, so it can be answered, not locked.
 */
export const BOSS_DELAY_MS = 1500;
/** Statuses a boss shrugs off entirely: it neither stands dazed nor lets itself be led. */
export const BOSS_IMMUNE = new Set(['stunned', 'taunted']);
/**
 * Lay a state, or renew it. A renewal keeps the longer end and the stronger
 * power, so recasting never shortens nor weakens what is already there; the
 * source becomes the latest caster (a second taunt takes the creature over).
 */
export function applyStatus(book, status, laid) {
    const current = book.get(status);
    const until = laid.now + laid.durationMs;
    const next = current === undefined || current.until <= laid.now
        ? { until, power: laid.power, source: laid.source }
        : {
            until: Math.max(current.until, until),
            power: Math.max(current.power, laid.power),
            source: laid.source,
        };
    book.set(status, next);
    return next;
}
/** The state if it still holds at `now`. */
export function activeStatus(book, status, now) {
    const current = book?.get(status);
    return current !== undefined && current.until > now ? current : undefined;
}
/** Forget what has worn off; returns the states that just ended. */
export function expireStatuses(book, now) {
    const ended = [];
    for (const [status, active] of book) {
        if (active.until <= now) {
            book.delete(status);
            ended.push(status);
        }
    }
    return ended;
}
/** A blow taken by a guarded player: its share taken away, still at least a point. */
export function guardedDamage(book, amount, now) {
    const guard = activeStatus(book, 'guarded', now);
    if (guard === undefined || amount <= 0)
        return amount;
    return Math.max(1, Math.round(amount * (1 - Math.min(1, guard.power))));
}
/** A player's dps while enraged. */
export function enragedDps(book, dps, now) {
    const rage = activeStatus(book, 'enraged', now);
    return rage === undefined ? dps : dps * (1 + rage.power);
}
/** A creature's pace (ms per tile) while slowed. */
export function slowedPace(book, paceMs, now) {
    const slow = activeStatus(book, 'slowed', now);
    return slow === undefined ? paceMs : paceMs * Math.max(1, slow.power);
}
/** Whether a creature stands dazed: it neither walks nor strikes. */
export function isStunned(book, now) {
    return activeStatus(book, 'stunned', now) !== undefined;
}
/** The player a taunted creature must fight, if it is taunted. */
export function tauntedBy(book, now) {
    return activeStatus(book, 'taunted', now)?.source;
}
/**
 * One tick of a bleed: its share of the caster's basic hit, at least a
 * point. The server calls it every `BLEED_TICK_MS` while the bleed holds.
 */
export function bleedDamage(power, basicHit) {
    return Math.max(1, Math.round(basicHit * power));
}
//# sourceMappingURL=statuses.js.map